import { Component, ContentType } from 'contensis-core-api';
import { Entry, TagGroup } from 'contensis-management-api';
import pl from 'pluralize';
import clone from 'rfdc';
import { AssetEntryBuilder } from './AssetEntryBuilder';
import { EntryField, IEntryFields } from './EntryFieldBuilder';
import { FirstPassStub } from './FirstPassStub';
import mapErrorToObject from './mapAppErrorToObject';
import {
  IContensisContext,
  MigrateEntryModel,
  MigrateModelsEntityResult,
  MigrateStatus,
  Model,
  NulledEntry,
} from '~/models';
import {
  EntryDependencyMeta,
  EntryDependencyOrigin,
} from '~/models/EntryDependencies';
import ContensisRepository from '~/repositories/ContensisRepository';
import ContentTypeHelper from '~/util/content-type/ContentTypeHelper';
import { compareFields, transformContentTypeId } from '~/util/EntryHelper';
import { Logger } from '~/util/logger';
import { LIB_VERSION } from '~/util/version';

/**
 * Build a complete entry specifically for the target repository, providing
 * context of source and target repositories we can check each field in the
 * given source entry for any dependencies and set a two-pass loading status
 * if any dependencies do not exist
 *
 * Output the entry as JSON for loading into the Management API using the `toJSON` method
 */
export class EntryBuilder implements MigrateEntryModel {
  contensis: ContensisRepository;
  source: ContensisRepository;

  contentType?: ContentType;
  sourceContentType?: ContentType;
  contentTypeHelper!: ContentTypeHelper;
  contentTypes: ContentType[] = [];
  components: Component[] = [];
  tagGroups: TagGroup[] = [];
  dependencies: EntryDependencyMeta[] = [];
  dependencyOf: EntryDependencyOrigin[] = [];
  existing?: Entry;
  model?: Model;
  sourceEntry: Entry;
  fields: IEntryFields = {};
  status!: MigrateStatus;
  result?: keyof MigrateModelsEntityResult;
  error?: MappedError;
  diff?: string;
  unmappedFields: IEntryFields = {};
  /** log any issues with building the entry */
  warnings: string[] = [];

  private sysId?: string; // If we need to override the default transformed id
  protected isStubContentType = false;
  private _requireSecondPass = false;

  /**
   * a good indicator that the entry is being created for a
   * default entry in a model migration
   */
  get builtContentType() {
    return this.contensis.models.migrate.contentTypes.find(
      (c) => c.id === this.sourceEntry.sys.contentTypeId
    );
  }
  get contentTypeId() {
    return this.sourceEntry.sys.contentTypeId;
  }
  get entryTitle() {
    return this.sourceEntry.entryTitle;
  }
  get id() {
    return this.sysId || this.contensis.transformGuid(this.originalId);
  }
  set id(val) {
    this.sysId = val;
  }
  get isPublished() {
    return this.sourceEntry.sys.isPublished;
  }
  get originalId() {
    return this.sourceEntry.sys.id || '';
  }
  get requireSecondPass() {
    if (this._requireSecondPass) return true;

    return Object.values(this.fields).some((bf) => {
      if (Array.isArray(bf))
        return bf.some((f) => {
          if (f.requireSecondPass) return true;
        });
      else if (bf.requireSecondPass) return true;
    });
  }
  /**
   * We might be creating an entry for a content type that
   * does not yet exist but is part of this migration instance
   */
  get willContentTypeExist() {
    if (this.contentType) return true;
    const builtContentType = this.builtContentType;
    return (
      builtContentType &&
      ['create', 'two-pass'].includes(builtContentType.status)
    );
  }

  constructor(
    contensis: ContensisRepository,
    source: ContensisRepository,
    sourceEntry: Entry
  ) {
    this.contensis = contensis;
    this.source = source;
    this.sourceEntry = sourceEntry;
    this.init();
  }

  /**
   * Assert if the EntryBuilder instance is of AssetEntryBuilder type
   */
  isAsset = (): this is AssetEntryBuilder => {
    return this.sourceEntry.sys.dataFormat === 'asset';
  };

  init = () => {
    const { contensis, contentTypeId, originalId, source } = this;

    // Find existing entry in target
    // We may have set existing from outside
    if (!this.existing) {
      // Migrating default entries with models keeps entries in context
      // vs. classic entries migration will populate entries.raw
      // const entries = contensis.context.entries.length
      //   ? contensis.context.entries
      //   : contensis.entries.raw;
      this.existing = contensis.entries.findDependent(this.sourceEntry || '');
    }

    // Find the model in target
    this.model = contensis.context.models?.find((m) => m.id === contentTypeId);

    // Resolve all dependent content types and components
    if (contentTypeId)
      this.contentTypes = contensis.context.resolve.contentTypes([
        contentTypeId, // also resolve this entry's content type
        ...(this.model?.contentTypes || []),
      ]);

    const componentIds = Array.from(
      new Set(
        [
          this.model?.components || [],
          this.model?.dependencies?.components?.map((c) => c[0]) || [],
        ].flat()
      )
    );
    this.components = this.model
      ? contensis.context.resolve.components(componentIds)
      : contensis.context.components;

    const tagGroupIds = Array.from(
      new Set(
        [
          this.model?.tagGroups || [],
          this.model?.dependencies?.tagGroups?.map((g) => g[0]) || [],
        ].flat()
      )
    );

    this.tagGroups = this.model
      ? contensis.context.resolve.tagGroups(tagGroupIds)
      : contensis.context.tagGroups;

    // Content type helper scoped with this entry's dependencies only
    this.contentTypeHelper = new ContentTypeHelper(
      this.contentTypes,
      this.components,
      this.tagGroups,
      contensis.project
    );
    // Add other contextual data
    (this.contentTypeHelper as IContensisContext).source = source;
    (this.contentTypeHelper as IContensisContext).payload =
      contensis.context.payload;

    // Find the content type in target
    this.contentType = contensis.context.resolve.contentType(contentTypeId);
    // Find the content type in source, and use the target content type if source is missing
    this.sourceContentType =
      source.context.resolve.contentType(contentTypeId) || this.contentType;

    // Get and clone the dependencies for this entry
    // so we can update them later without mutating the source context
    // TODO: consider node and tag dependencies too
    this.dependencies = clone()(
      source.entries.dependencies.entryIds[originalId || ''] || []
    );

    this.dependencyOf =
      source.entries.dependencies.dependencyIds[originalId || ''] || [];
  };

  /**
   * Iterate over built entry fields and return a the entry as JSON
   * @returns entry as JSON
   */
  toJSON(opts = { firstPass: false }) {
    let entry: Entry;
    if (this.isStubContentType && !this.contentType) {
      entry = FirstPassStub.entry(this.sourceEntry);
    } else {
      const { entryTitleField } = this.contentType || {};
      // Map entry fields
      entry = Object.entries(this.fields).reduce((prev, [k, v]) => {
        if (Array.isArray(v)) {
          prev[k] = [];
          for (const inner of v) {
            prev[k].push(inner.toJSON(opts));
          }
        } else {
          const val = v.toJSON(opts);
          prev[k] = val;

          // set entryTitle (handy for use in logging)
          if (v.field.id === entryTitleField) prev.entryTitle = val;
        }
        return prev;
      }, {} as any);
    }
    // Create a sys object based on the source entry
    entry.sys = this.buildSysObject();

    // Add sys.metadata fingerprint
    this.mapEntryMetadata(entry);

    // Update the sys.id (if transformGuids is true)
    if (entry.sys.id) entry.sys.id = this.contensis.transformGuid(entry.sys.id);

    if (Object.keys(this.contensis.transformContentTypes).length)
      return transformContentTypeId(
        entry,
        this.contensis.transformContentTypes
      );

    return entry;
  }

  /**
   * Check we have the content models available to process this entry
   */
  validate = () => {
    if (!(this.contentType && this.model)) {
      if (this.willContentTypeExist) this.isStubContentType = true;
      else
        this.error = mapErrorToObject(
          new Error(
            `Content type '${this.contentTypeId}' does not exist in project '${this.contensis.project.id}'`
          )
        );
    }
  };

  /**
   * Iterate all fields in the content type taking the values from the source entry
   * adding each field to the built entry fields
   */
  build = () => {
    try {
      // Build the entry and set the migrate status
      this.validate();

      for (const sourceField of this.sourceContentType?.fields || []) {
        // Locate this source field in the target content type
        const field =
          // We might have assigned the source content type to be the same as the target if source was absent
          this.contentType === this.sourceContentType
            ? sourceField
            : (this.contentType?.fields || []).find(
                (f) => f.id === sourceField.id
              );
        if (!field) {
          const missingField = new EntryField(
            sourceField,
            this.sourceEntry[sourceField.id],
            this.sourceEntry,
            this.contentTypeHelper as IContensisContext,
            this.contensis,
            this.dependencies.filter(
              (d) => d.fieldIdPath.split('.')[0] === sourceField.id
            )
          );

          if (
            Logger.shouldLog(
              missingField.dependencies.length ? 'info' : 'debug'
            )
          )
            // only push this warning if log level is debug or lower
            // it can be noisy if there are many entries with missing fields
            // bump up to info if the unmapped field contains dependencies
            this.warnings.push(
              `${Logger.helpText(sourceField.id)}: Incomplete ${Logger.infoText(`[field missing in target content type${missingField.dependencies.length ? ` with ${pl(`dependency`, missingField.dependencies.length)}: ${missingField.dependencies.map((d) => d.entryId).join(', ')}` : ''}]`)}`
            );
          // Set all dependencies of the missing field as dirty so they can be re-evaluated for migration
          for (const dep of missingField.dependencies) dep.isDirty = true;

          this.unmappedFields[sourceField.id] = missingField;
          continue;
        }

        const entryField = new EntryField(
          field,
          this.sourceEntry[field.id],
          this.sourceEntry,
          this.contentTypeHelper as IContensisContext,
          this.contensis,
          this.dependencies.filter(
            (d) => d.fieldIdPath.split('.')[0] === sourceField.id
          )
        );
        this.fields[field.id] = entryField;
        this.warnings.push(
          ...entryField.warnings.map(
            (w) => `${Logger.helpText(entryField.fieldIdPath)}: ${w}`
          )
        );
      }
    } catch (ex: any) {
      this.error = mapErrorToObject(ex);
      this.error.message = `Error ${this.contensis.logGuid(this.sourceEntry)}: ${this.error.message}`;
    }
    this.status = this.checkStatus();
  };

  /**
   * Create a new sys object for the entry based on the source sys
   */
  buildSysObject = () => {
    const newSys = {
      ...this.sourceEntry.sys,
      isPublished: false,
    };
    // Remove attributes that could be incorrect when creating the entry
    // we will merge the sys field from any existing entry in the target later on
    delete newSys.projectId;
    delete newSys.slug;
    delete newSys.version;
    delete (newSys as any).versionStatus;
    return newSys;
  };

  /**
   * Add sys.metadata fingerprint to built entries
   */
  mapEntryMetadata = (entry: Entry, source = this.source) => {
    const metadata =
      entry.sys.dataFormat !== 'asset' && (entry.sys as any).metadata
        ? (entry.sys as any).metadata
        : ({} as any);

    metadata.originalCms = source.alias;
    metadata.originalProjectId = source.project?.id;
    metadata.originalId = entry.sys.id;
    metadata.createdBy = `Migratortron-1000 v${LIB_VERSION}`;

    (entry.sys as any).metadata = metadata;
  };

  shouldPublish = (source: Entry, target: Entry) =>
    // Compare the source and target entry to check if we are working
    // with a published source and decide whether the target entry should be published
    source.sys.isPublished &&
    (target?.sys.isPublished !== true ||
      (source.sys.version?.versionNo?.endsWith('.0') &&
        !target?.sys.version?.versionNo?.endsWith('.0')));

  /**
   * Check the entry for update status
   */
  checkStatus = (): MigrateStatus => {
    const { contensis, error, existing, sourceEntry } = this;

    if (error) return 'error';

    // If this entry is part of defaults in a model migration, and it has been
    // superceded by an existing default we will ignore it
    if (
      contensis.models.toMigrate.find((c) =>
        c.rejectedDefaults.find((rd) => rd[0] === sourceEntry.sys.id)
      ) &&
      !contensis.models.toMigrate.find((c) =>
        c.defaults.find((rd) => rd[0] === this.id)
      )
    )
      return 'ignore';

    if (existing) {
      // Has the entry changed from the existing?
      const entry = this.toJSON();

      const { changed, diff } = compareFields(
        this.nullFields(entry),
        this.nullFields(existing),
        this.source,
        contensis
      );

      if (changed) {
        this.diff = diff;
        return 'update';
      } else {
        if (this.shouldPublish(sourceEntry, existing)) return 'publish';
        return 'no change';
      }
    }

    if (this.requireSecondPass) return 'two-pass';

    return 'create';
  };
  /**
   * Set Contensis controlled fields to null before doing a diff as these will always be different
   */
  nullFields(e: Entry): NulledEntry {
    return {
      ...e,
      entryTitle: null,
      entryDescription: null,
      entryTags: null,
      sys:
        Object.keys((e.sys as any).schedules || {}).length > 0
          ? { schedules: (e.sys as any).schedules }
          : null,
    } as NulledEntry;
  }
}
